import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./FeedbackModal.scss";

interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  userScore: number;
  passScore: number;
  title?: string;
  message?: string;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  visible,
  onClose,
  userScore,
  passScore,
  title,
  message,
  setShowModal,
  showModal,
}) => {
  useEffect(() => {
    setShowModal(visible);
    if (visible) {
      const timer = setTimeout(() => {
        setShowModal(false);
        onClose();
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  const passed = userScore >= passScore;

  const defaultTitle = useMemo(() => {
    if (userScore >= 97) return "Outstanding!";
    if (userScore >= 90) return "Excellent";
    if (passed) return "Great Job!";
    return "Good Effort!";
  }, [userScore, passed]);

  const defaultMessage = useMemo(() => {
    if (userScore >= 97) return "Incredible! You unlocked a new level!";
    if (userScore >= 90) return "Awesome! You scored higher than 90% of users.";
    if (passed) return "Well done — you unlocked a new level!";
    return "Keep practicing, you’re getting closer!";
  }, [userScore, passed]);

  if (!showModal) return null;
  return createPortal(
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <video
            className="video-background"
            src={require("../../assets/background.mp4")}
            autoPlay
            loop
            muted
          />
          <motion.div
            className="modal-container"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="trophy-circle">
              <img
                src={
                  userScore < 30
                    ? require("../../assets/mama-pluri-sad.gif")
                    : require("../../assets/mama-pluri.gif")
                }
                alt="trophy"
              />
            </div>

            <div className="title-container">
              <h2>{title ?? defaultTitle}</h2>
              <p className="subtitle">Keep practicing to improve</p>
            </div>

            <div className="score-card">
              <div className="score-column">
                <span className="label">Your Score</span>
                <span className="user-score">{userScore}</span>
              </div>
              <div className="score-column">
                <span className="label">Pass Score</span>
                <span className="pass-score">{passScore}</span>
              </div>
            </div>

            <p className="message">{message ?? defaultMessage}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default FeedbackModal;
